import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';
import { CreateChancel, FunderAddress, Input, MaskInput, MaskInputCustom, SelectAutocompleteMultiple } from 'components';
import { fundingSourceActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { EmailValidator, FindLoad, FindErrorItem, OnlyNumber, useModal, FindSuccessItem, Images } from 'utils';
import { payerTypeOptions } from 'pages';
import { authService } from '../../../../store/fundingSource/fundingSource.service';
import { clearingHouseTypeEnums } from '../constant';

export const CreateFundingSource = ({ info, closeToSuccess }) => {
    const ACTION_TYPE = info ? 'EDIT_FUNDING_SOURCE' : 'CREATE_FUNDING_SOURCE';
    const dispatch = useDispatch();
    const [enteredAddress, setEnteredAddress] = useState(info?.address ? info?.address : null);
    const success = FindSuccessItem(ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { close } = useModal();
    const {
        handleSubmit,
        watch,
        control,
        setError,
        clearErrors,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            payerIds: [
                {
                    id: '',
                    clearinghouse: {},
                },
            ],
        },
    });
    const [shortNameExist, setShortNameExist] = useState(false);
    const [loadShortName, setLoadShortName] = useState(false);
    const [loadCapabilities, setLoadCapabilities] = useState(false);
    const [suggestionList, setSuggestionList] = useState([]);
    const [showList, setShowList] = useState(false);
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'payerIds',
    });

    useEffect(() => {
        if (info) {
            const result = {
                ...info,
            };
            delete result?.canDelete;
            delete result?.termination;

            const formattedPayerIds = {};
            (result.payerIds || []).forEach((payer) => {
                if (payer?.clearinghouse && payer?.id) {
                    formattedPayerIds[payer.clearinghouse] = {
                        id: payer.id,
                        clearinghouse: payer.clearinghouse,
                    };
                }
            });

            reset({
                ...result,
                payerIds: formattedPayerIds,
            });
        } else {
            reset({
                payerIds: [],
            });
        }
    }, [info, reset]);

    useEffect(() => {
        return () => {
            clearErrors();
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
            if (closeToSuccess) {
                closeToSuccess();
            }
        }
    }, [success]);

    useEffect(() => {
        if (backError?.error?.length) {
            if (backError?.error[0] === 'website must be a URL address') {
                setError('website', {
                    message: 'Website must be a URL address.',
                });
                dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            }
        }
        if (backError?.error?.length) {
            if (backError?.error[0] === 'phoneNumber must be a valid phone number') {
                setError('phoneNumber', {
                    message: 'Phone number must be a valid phone number',
                });
                dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            }
        }
        if (backError?.error === 'Funder already exists') {
            setError('email', {
                message: 'Payer already exists.',
            });
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        }
    }, [backError]);

    const handleCreate = (data) => {
        if (watch('shortName')?.length > 5) {
            setError('shortName', { message: 'Short Name must be at most 5 characters long.' });
            return;
        }
        if (shortNameExist) {
            setError('shortName', { message: 'Short Name already exist' });
            return;
        }

        const payerIdsArray = Object.values(data.payerIds || {}).filter((payer) => payer?.id && payer?.clearinghouse);

        const result = _.omitBy(
            {
                ...data,
                payerIds: payerIdsArray,
            },
            (value) =>
                typeof value === 'undefined' ||
                (typeof value === 'string' && !value?.trim()) ||
                (typeof value === 'object' && _.isEmpty(value))
        );

        const sandedData = {
            ...result,
            status: info ? info?.status : 'ACTIVE',
            address: enteredAddress,
        };

        if (info) {
            dispatch(fundingSourceActions.editFundingSource(info.id, sandedData));
        } else {
            dispatch(fundingSourceActions.createFundingSource(sandedData));
        }
    };

    const checkPayerId = (e) => {
        const ifFromPayer = info?.shortName === e.target.value;
        if (ifFromPayer) {
            setShortNameExist(false);
            return;
        }
        if (e.target.value && e.target.value.length <= 5) {
            setLoadShortName(true);
            authService
                .checkPayerId(e.target.value)
                .then((res) => {
                    setShortNameExist(res.data);
                    if (res?.data) {
                        setError('shortName', {
                            message: 'Short Name already exist',
                        });
                    } else {
                        clearErrors('shortName');
                    }
                })
                .finally(() => {
                    setLoadShortName(false);
                });
        } else {
            setError('shortName', {
                message: 'Short Name must be at most 5 characters long.',
            });
        }
    };

    const checkCapabilities = (e) => {
        const payerName = e?.target?.value;
        if (!payerName) {
            return;
        }
        if (payerName) {
            setShowList(true);
            setLoadCapabilities(true);
            authService
                .getPayerCapabilitiesService({
                    query: payerName,
                    pageSize: 50,
                })
                .then((res) => {
                    setSuggestionList(res?.data?.items);
                })
                .finally(() => {
                    setLoadCapabilities(false);
                });
        }
    };

    return (
        <div style={{ width: 860 }}>
            <p className="modal-header-title">{info ? 'Edit Payer' : 'Add Payer'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper" style={{ marginBottom: 28 }}>
                    <p className="modal-box-title" style={{ marginTop: '-12px' }}>
                        Payer Info
                    </p>
                    <div className="flex-align-start" style={{ gap: '24px' }}>
                        <div className="full-width">
                            <div style={{ position: 'relative' }}>
                                <Input
                                    onChange={checkCapabilities}
                                    name="name"
                                    label="Payer Name*"
                                    variant="outlined"
                                    control={control}
                                    maxLength={40}
                                    rules={{
                                        maxLength: {
                                            value: 40,
                                            message: 'Max length is 40 digits.',
                                        },
                                        required: 'Payer name is required.',
                                    }}
                                />

                                {showList && (
                                    <div style={{ position: 'relative' }}>
                                        <div className="suggestion-list-wrapper">
                                            {loadCapabilities ? (
                                                <div style={{ marginBottom: 8 }}>Loading...</div>
                                            ) : (
                                                <div className="flex-end">
                                                    <button
                                                        className="reset-btn-by-default"
                                                        type={'button'}
                                                        onClick={() => setShowList(false)}
                                                    >
                                                        <img src={Images.closeGray} alt="close" />
                                                    </button>
                                                </div>
                                            )}

                                            {!!suggestionList?.length
                                                ? suggestionList.map((item, index) => (
                                                      <div
                                                          key={item?.payer?.primaryPayerId || index}
                                                          onClick={() => {
                                                              setValue('name', item?.payer?.displayName);
                                                              setValue(`payerIds.${clearingHouseTypeEnums.TL_CLEARINGHOUSE}`, {
                                                                  id: item?.payer?.primaryPayerId,
                                                                  clearinghouse: clearingHouseTypeEnums.TL_CLEARINGHOUSE,
                                                              });
                                                              setShowList(false);
                                                              setSuggestionList([]);
                                                          }}
                                                      >
                                                          <p>{item?.payer?.displayName}</p>
                                                      </div>
                                                  ))
                                                : !loadCapabilities && <p>N/A</p>}
                                        </div>
                                    </div>
                                )}

                                {showList && <div className="beck-drop" onClick={() => setShowList(false)} />}
                            </div>
                            <Input
                                handleBlur={checkPayerId}
                                name="shortName"
                                label="Short Name*"
                                placeholder="Short Name must be at most 5 characters long."
                                variant="outlined"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: 5,
                                        message: 'Short Name must be at most 5 characters long.',
                                    },
                                    required: 'Short Name is required.',
                                }}
                                maxLength={5}
                            />
                            <Input
                                name="contactTitle"
                                label="Contact Title"
                                variant="outlined"
                                control={control}
                                maxLength={60}
                                rules={{
                                    maxLength: {
                                        value: 60,
                                        message: 'Max length is 60 digits.',
                                    },
                                }}
                            />
                            <MaskInputCustom
                                name="phoneNumber"
                                label="Phone Number"
                                control={control}
                                rules={{
                                    pattern: {
                                        value: OnlyNumber,
                                        message: 'Please enter a valid 10 digit number.',
                                    },
                                    minLength: {
                                        value: 10,
                                        message: 'Please enter a valid 10 digit number.',
                                    },
                                }}
                            />
                            <Input
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                control={control}
                                rules={{
                                    pattern: {
                                        value: EmailValidator,
                                        message: 'Valid email address is required (e.g., name@example.com).',
                                    },
                                }}
                            />
                        </div>
                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                options={payerTypeOptions}
                                renderValue={(option) => option?.name}
                                label={'Type*'}
                                name="type"
                                control={control}
                                rules={{
                                    required: 'Please select a type from the dropdown.',
                                }}
                            />
                            <Input
                                name="contact"
                                label="Contact Person"
                                variant="outlined"
                                control={control}
                                maxLength={60}
                                rules={{
                                    maxLength: {
                                        value: 60,
                                        message: 'Max length is 60 digits.',
                                    },
                                }}
                            />
                            <Input
                                name="contactEmail"
                                label="Contact Email"
                                variant="outlined"
                                control={control}
                                rules={{
                                    pattern: {
                                        value: EmailValidator,
                                        message: 'Valid email address is required (e.g., name@example.com).',
                                    },
                                }}
                            />
                            <Input
                                name="vendor"
                                label="Vendor ID"
                                variant="outlined"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: 30,
                                        message: 'Max length is 20 digits.',
                                    },
                                }}
                                maxLength={30}
                            />
                            <Input name="website" label="Website" variant="outlined" control={control} />
                            {/*<Input*/}
                            {/*    name="poBox"*/}
                            {/*    label="P.O. Box"*/}
                            {/*    variant="outlined"*/}
                            {/*    control={control}*/}
                            {/*/>*/}
                        </div>
                    </div>

                    <div>
                        <p className="modal-box-title" style={{ marginBottom: 8 }}>
                            Address
                        </p>
                        <FunderAddress
                            notRequired={true}
                            placeholder={'Street Address or P.O Box'}
                            selectedAddress={info?.address}
                            setCurrentAddress={setEnteredAddress}
                        />
                    </div>

                    <div>
                        <p className="modal-box-title" style={{ marginBottom: 8 }}>
                            Payer IDs
                        </p>
                        <div className="flex-align-center" style={{ gap: 24 }}>
                            <Input
                                name={`payerIds.${clearingHouseTypeEnums.TL_CLEARINGHOUSE}.id`}
                                label="TL Clearinghouse Payer ID"
                                variant="outlined"
                                control={control}
                                onChange={(e) => {
                                    setValue(`payerIds.${clearingHouseTypeEnums.TL_CLEARINGHOUSE}`, {
                                        id: e.target.value,
                                        clearinghouse: clearingHouseTypeEnums.TL_CLEARINGHOUSE,
                                    });
                                }}
                            />
                            <Input
                                name={`payerIds.${clearingHouseTypeEnums.AVAILITY}.id`}
                                label="Availity Payer ID"
                                variant="outlined"
                                control={control}
                                onChange={(e) => {
                                    setValue(`payerIds.${clearingHouseTypeEnums.AVAILITY}`, {
                                        id: e.target.value,
                                        clearinghouse: clearingHouseTypeEnums.AVAILITY,
                                    });
                                }}
                            />
                            <Input
                                name={`payerIds.${clearingHouseTypeEnums.OFFICE_ALLY}.id`}
                                label="Office Ally Payer ID"
                                variant="outlined"
                                control={control}
                                onChange={(e) => {
                                    setValue(`payerIds.${clearingHouseTypeEnums.OFFICE_ALLY}`, {
                                        id: e.target.value,
                                        clearinghouse: clearingHouseTypeEnums.OFFICE_ALLY,
                                    });
                                }}
                            />
                        </div>
                    </div>

                    <div>
                        {/*<div>*/}
                        {/*    <p className="modal-box-title">Select Capabilities to Enroll</p>*/}
                        {/*    <div className="capabilities-for-enroll-box">*/}
                        {/*        /!*<div className="flex-align-center full-width" style={{ gap: 16, marginTop: '-12px' }}>*!/*/}
                        {/*        {payerCapabilitiesEnumList?.map((item, j) => {*/}
                        {/*            const error = errors?.payerIds?.[j]?.useCases;*/}
                        {/*            return <div key={j} className="capabilities-for-enroll-items"*/}
                        {/*                        style={{ marginTop: -12 }}>*/}
                        {/*                <CheckboxCustom*/}
                        {/*                    color={error ? '#FE7070' : '#D0D5DD'}*/}
                        {/*                    key={item.value}*/}
                        {/*                    name={`payerIds.${j}.useCases.${item.value}`}*/}
                        {/*                    label={item.title}*/}
                        {/*                    control={control}*/}
                        {/*                    valueOnCheck={item.value}*/}
                        {/*                    rules={{*/}
                        {/*                        validate: () => {*/}
                        {/*                            const useCases = watch(`payerIds.${j}.useCases`);*/}
                        {/*                            const selected = Object.values(useCases || {}).filter(Boolean);*/}
                        {/*                            return selected.length > 0 || 'At least one use case must be selected.';*/}
                        {/*                        },*/}
                        {/*                    }}*/}
                        {/*                />*/}
                        {/*                <p className="capabilities-for-enroll-info">Enrollment Required</p>*/}
                        {/*            </div>;*/}
                        {/*        })}*/}
                        {/*        /!*</div>*!/*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        {/*<div className="space-between" style={{ marginBottom: 8 }}>*/}
                        {/*    <div className="flex-align-center" style={{ gap: 8 }}>*/}
                        {/*        <p className="modal-box-title" style={{ marginBottom: 2 }}>Payer ID & Use Cases</p>*/}
                        {/*        <ToolInfo*/}
                        {/*            text={'You can enter one ID for all selected purposes, or add separate IDs for each.'} />*/}
                        {/*    </div>*/}
                        {/*    <AddButtonLight*/}
                        {/*        addButnLightInnerText={'Add another ID'}*/}
                        {/*        onAddButnLightClick={async () => {*/}
                        {/*            const lastIndex = watch('payerIds')?.length - 1;*/}
                        {/*            const lastPayerPath = `payerIds.${lastIndex}`;*/}
                        {/*            const isValid = await trigger([*/}
                        {/*                `${lastPayerPath}.id`,*/}
                        {/*                `${lastPayerPath}.useCases.CLAIM_SUBMISSION`,*/}
                        {/*                `${lastPayerPath}.useCases.STATUS_INQUIRY`,*/}
                        {/*                `${lastPayerPath}.useCases.ELIGIBILITY_CHECK`,*/}
                        {/*            ]);*/}

                        {/*            if (!isValid) return;*/}

                        {/*            append({*/}
                        {/*                id: '',*/}
                        {/*                useCases: {*/}
                        {/*                    CLAIM_SUBMISSION: null,*/}
                        {/*                    STATUS_INQUIRY: null,*/}
                        {/*                    ELIGIBILITY_CHECK: null,*/}
                        {/*                },*/}
                        {/*            });*/}
                        {/*        }}*/}
                        {/*    />*/}
                        {/*</div>*/}

                        {/*{fields?.map((i, index) => (*/}
                        {/*    <div key={index}>*/}
                        {/*        {index !== 0 &&*/}
                        {/*            <div className="flex-end">*/}
                        {/*                <button*/}
                        {/*                    className="remove-payer"*/}
                        {/*                    type="button"*/}
                        {/*                    onClick={() => remove(index)}*/}
                        {/*                 >*/}
                        {/*                    <img src={Images.minusRemoveCircle} alt="remove" />*/}
                        {/*                    <p>Remove</p>*/}
                        {/*                </button>*/}
                        {/*            </div>*/}
                        {/*        }*/}
                        {/*        <Input*/}
                        {/*            name={`payerIds.${[index]}.id`}*/}
                        {/*            label="Payer ID*"*/}
                        {/*            variant="outlined"*/}
                        {/*            control={control}*/}
                        {/*            maxLength={5}*/}
                        {/*            rules={{*/}
                        {/*                required: 'Payer ID is required.',*/}
                        {/*            }}*/}
                        {/*        />*/}
                        {/*        <div className="flex-align-center" style={{ gap: 16, marginTop: '-12px' }}>*/}
                        {/*            {payerCaseEnumList?.map((item, j) => {*/}
                        {/*                const error = errors?.payerIds?.[index]?.useCases;*/}
                        {/*                return <Fragment key={j}>*/}
                        {/*                    <CheckboxCustom*/}
                        {/*                        color={error ? '#FE7070' : ''}*/}
                        {/*                        key={item.value}*/}
                        {/*                        name={`payerIds.${index}.useCases.${item.value}`}*/}
                        {/*                        label={item.title}*/}
                        {/*                        control={control}*/}
                        {/*                        valueOnCheck={item.value}*/}
                        {/*                        rules={{*/}
                        {/*                            validate: () => {*/}
                        {/*                                const useCases = watch(`payerIds.${index}.useCases`);*/}
                        {/*                                const selected = Object.values(useCases || {}).filter(Boolean);*/}
                        {/*                                return selected.length > 0 || 'At least one use case must be selected.';*/}
                        {/*                            },*/}
                        {/*                        }}*/}
                        {/*                    />*/}
                        {/*                </Fragment>;*/}
                        {/*            })}*/}
                        {/*        </div>*/}
                        {/*    </div>*/}
                        {/*))}*/}
                    </div>
                </div>

                <CreateChancel
                    disabled={loadShortName}
                    type="submit"
                    loader={!!loader.length}
                    create={info ? 'Save' : 'Add'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
